import { useEffect, useState } from 'react';

import axiosClient from '../../../api/axios-client';
import { TwoFactorSetupResponse } from '../../../api/twoFactor/types';
import { formatTotpSecret } from '../../../helpers/totpSecret';

import Button from '../../components/Button';
import Text from '../../components/Text';
import { t } from '../../i18n/langue';

interface ConfigurationApplicationProps {
    challengeId: string;
    /** Appelé quand la clé est prête : le champ du code peut s'activer. */
    onPrete: () => void;
    onErreur: (erreur: unknown) => void;
    /** Compte autorisé à basculer sur l'e-mail (absent : aucune application n'est requise). */
    onUtiliserEmail?: () => void;
}

/**
 * Ajout de TriggerFlow dans l'application d'authentification, depuis le
 * téléphone lui-même : on ne scanne pas son propre écran, d'où le lien
 * `otpauth://` et la clé en clair à recopier si aucune application ne répond.
 */
export default function ConfigurationApplication({ challengeId, onPrete, onErreur, onUtiliserEmail }: ConfigurationApplicationProps) {
    const [config, setConfig] = useState<TwoFactorSetupResponse | null>(null);
    const [echec, setEchec] = useState(false);

    const charger = () => {
        setEchec(false);
        axiosClient
            .post<TwoFactorSetupResponse>('/login/two-factor/setup', { challenge_id: challengeId })
            .then(({ data }) => {
                setConfig(data);
                onPrete();
            })
            .catch((e) => {
                setEchec(true);
                onErreur(e);
            });
    };

    useEffect(() => {
        charger();
        // Une génération par challenge (le serveur est de toute façon idempotent).
    }, [challengeId]);

    if (echec) {
        return (
            <div className="flex flex-col gap-3">
                <Button libelle={t('commun.reessayer')} variante="secondaire" onPress={charger} pleineLargeur />
                {onUtiliserEmail && (
                    <Button libelle={t('connexion.recevoirParEmail')} variante="lien" onPress={onUtiliserEmail} />
                )}
            </div>
        );
    }

    if (!config) {
        return <div className="h-32 animate-pulse rounded-xl bg-main-bg-color" />;
    }

    return (
        <div className="flex flex-col gap-3 rounded-xl bg-main-bg-color p-4">
            <a
                href={config.otpauth_uri}
                className="inline-flex min-h-touch items-center justify-center rounded-xl bg-main-color px-4 text-sm font-semibold text-white"
            >
                {t('connexion.ajouterAMonApplication')}
            </a>
            <Text variante="petit" ton="attenue" className="block">{t('connexion.ouSaisissezCle')}</Text>
            <Text variante="petit" className="block break-all font-mono tracking-wide">
                {formatTotpSecret(config.secret)}
            </Text>
            <Button
                libelle={t('connexion.copierLaCle')}
                variante="lien"
                onPress={() => void navigator.clipboard?.writeText(config.secret).catch(() => undefined)}
            />
            {onUtiliserEmail && (
                <Button libelle={t('connexion.recevoirParEmail')} variante="lien" onPress={onUtiliserEmail} />
            )}
        </div>
    );
}
